import { useCallback, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import isEqual from 'lodash/isEqual'

import {
  selectCloneLeaguesSession,
  selectCloneLeaguesSessionID,
  selectCloneLeaguesSessionData,
  selectSessionSaverLoading,
  selectSessionSaverError,
  addLeaguesToSession,
  removeLeaguesFromSession,
  updateLeagueConfig,
  updateAllLeaguesConfigs,
  saveSession,
} from '../reducers/session_saver'
import { selectSelectedLeaguesForCloning } from '../reducers/selected_leagues_for_cloning'
import { selectCloningStep } from '../reducers/cloning_steps'
import { selectProductType, selectTextSearch } from '../reducers/leagues_filter_values'
import { selectConfigsOptions, selectConfigsSeason } from '../reducers/leagues_configs'
import { selectEnhancedLeagues } from '../reducers/enhanced_leagues'
import {
  translateTimeSeries,
  calculateFutureEquivalentDate,
  parseDateUTC,
  todayUTC,
  toISODateString,
} from '../timeUtils'
import { ALL_ENTRIES_FILTER_VALUE } from '../constants'
import { startCloneLeaguesSession } from '../reducers/clone_league_session_starter'

const useSessionSaver = () => {
  const dispatch = useDispatch()

  const cloningStep = useSelector(selectCloningStep)
  const session = useSelector(selectCloneLeaguesSession)
  const sessionID = useSelector(selectCloneLeaguesSessionID)
  const sessionData = useSelector(selectCloneLeaguesSessionData, isEqual)
  const isLoading = useSelector(selectSessionSaverLoading)
  const error = useSelector(selectSessionSaverError)
  const selectedLeagues = useSelector(selectSelectedLeaguesForCloning)
  const configsOptions = useSelector(selectConfigsOptions)
  const configsSeason = useSelector(selectConfigsSeason)
  const enhancedLeagues = useSelector(selectEnhancedLeagues)
  const textSearch = useSelector(selectTextSearch)
  const productType = useSelector(selectProductType)

  const mergedLeagues = useMemo(() => {
    const sessionLeagues = sessionData?.leagues || {}
    const enhancedLeaguesByID = (enhancedLeagues || []).reduce((acc, league) => {
      acc[String(league.id)] = league
      return acc
    }, {})

    return Object.entries(sessionLeagues).reduce((acc, [ id, sessionLeague ]) => {
      acc[id] = {
        ...enhancedLeaguesByID[id],
        ...sessionLeague,
      }
      return acc
    }, {})
  }, [ sessionData, enhancedLeagues ])

  // STEP 1 - Save selected leagues
  const saveSelectedLeaguesToSession = useCallback(() => {
    const currentLeagueIds = Object.keys(sessionData?.leagues || {})
    const selectedLeagueIds = selectedLeagues.map(league => String(league.id))

    const toRemove = currentLeagueIds.filter(id => !selectedLeagueIds.includes(id))
    const toAdd = selectedLeagues
      .filter(league => !currentLeagueIds.includes(String(league.id)))
      .reduce((acc, league) => {
        acc[String(league.id)] = {
          name: league.name,
          id: String(league.id),
          league_name: league.name,
          ...league,
        }
        return acc
      }, {})

    if (toRemove.length) {
      dispatch(removeLeaguesFromSession(toRemove))
    }
    if (Object.keys(toAdd).length) {
      dispatch(addLeaguesToSession(toAdd))
    }
  }, [ dispatch, sessionData, selectedLeagues ])

  // STEP 3 - Save selected configs
  const saveConfigsToSession = useCallback(() => {
    dispatch(updateAllLeaguesConfigs({ ...configsOptions, season: configsSeason.value }))
  }, [ dispatch, configsSeason.value, configsOptions ])

  const saveSessionToServer = useCallback(() => {
    dispatch(saveSession({
      cloneLeaguesSessionID: sessionID,
      data: { ...sessionData, cloningStep: cloningStep },
    }))
  }, [ dispatch, sessionID, sessionData, cloningStep ])

  // Step 4 - Leagues for date selection
  const leaguesForDatePicking = useMemo(() => {
    return Object.entries(mergedLeagues).reduce((acc, [ id, league ]) => {
      if (textSearch && !league.name?.toLowerCase().includes(textSearch.toLowerCase())) {
        return acc
      }
      if (productType && productType !== ALL_ENTRIES_FILTER_VALUE && league.product !== productType) {
        return acc
      }
      acc[id] = league
      return acc
    }, {})
  }, [ mergedLeagues, textSearch, productType ])

  const setMassDates = useCallback((leagueID, dates) => {
    dispatch(updateLeagueConfig(leagueID, { mass_dates: dates }))
  }, [ dispatch ])

  const addDateToMassDates = useCallback((leagueID, date) => {
    const currentMassDates = sessionData?.leagues?.[leagueID]?.mass_dates || []

    // TODO: persist the data for rounds in the session (NEEDED FOR VALIDATION)
    const leagueRounds = mergedLeagues?.[leagueID]?.rounds || []

    if (currentMassDates.length + 1 > leagueRounds.length) {
      return
    }

    setMassDates(leagueID, [ ...currentMassDates, date ])
  }, [ sessionData, mergedLeagues, setMassDates ])

  const removeDateFromMassDates = useCallback((leagueID, date) => {
    const currentMassDates = sessionData?.leagues?.[leagueID]?.mass_dates || []
    const firstMatch = currentMassDates.indexOf(date)
    if (firstMatch === -1) {
      return
    }
    setMassDates(leagueID, currentMassDates.filter((_, index) => index !== firstMatch))
  }, [ sessionData, setMassDates ])

  const prePopulateMassDates = useCallback(() => {
    const today = todayUTC()

    Object.entries(mergedLeagues).forEach(([ leagueID, league ]) => {
      const rounds = league?.rounds || []
      const currentMassDates = mergedLeagues?.[leagueID]?.mass_dates

      if (!rounds.length || currentMassDates?.length) {
        return
      }

      const roundDates = rounds
        .map(round => round.playAt)
        .sort()

      const firstRoundDate = parseDateUTC(roundDates[0])

      if (firstRoundDate >= today) {
        setMassDates(leagueID, roundDates)
      } else {
        const newStartDate = calculateFutureEquivalentDate(firstRoundDate, today)
        const translatedDates = translateTimeSeries(roundDates, newStartDate)
        const dates = translatedDates.map(d => toISODateString(d))
        setMassDates(leagueID, dates)
      }
    })
  }, [ mergedLeagues, setMassDates ])

  const startSessionJobs = useCallback(() => {
    dispatch(startCloneLeaguesSession({
      cloneLeaguesSessionID: sessionID,
      data: sessionData,
    }))
  }, [ sessionID, sessionData, cloningStep, dispatch ])

  return {
    session,
    sessionID,
    sessionData,
    isLoading,
    error,
    mergedLeagues,
    leaguesForDatePicking,
    saveSelectedLeaguesToSession,
    saveConfigsToSession,
    updateNameForLeague: (leagueID, newName) => dispatch(updateLeagueConfig(leagueID, { league_name: newName })),
    saveSessionToServer,
    startSessionJobs,
    setMassDates,
    addDateToMassDates,
    removeDateFromMassDates,
    prePopulateMassDates,
  }
}

export default useSessionSaver
